import React, { useState, useEffect, useCallback, useRef } from 'react';
import DatePicker, { registerLocale } from 'react-datepicker';
import { de } from 'date-fns/locale';
import 'react-datepicker/dist/react-datepicker.css';
import cx from 'classnames';
import { useAppDispatch, useAppSelector } from '../../../store';
import {
  setQuestionAnswer,
  setReturnQuestion,
  setCurrentIndex,
  setAvailableDays,
  resetSection,
} from '../../../store/slices/quizSlice';
import {
  QUESTION_KEYS,
  ANSWERS,
  SECTION_KEYS,
  SectionKey,
  VALIDATION,
  CARE_PERIODS,
  FREE_LENGTH_NUMBER_KEYS,
  CARE_LEVELS,
} from '../../../constants/quizConstants';
import {
  setCareRecipientField,
  setLegalRepresentativeField,
  setPreventedCaregiverField,
  setSubstituteCaregiverField,
  setCommercialProviderField,
  setPrivateIndividualField,
  setPrivateIndividual,
  resetCommercialProvider,
  resetPrivateIndividual,
} from '../../../store/slices/formDataSlice';
import { closeModal } from '../../../store/slices/uiSlice';
import { calculateAge } from '../../../utils/dateUtils';
import { getReturnMessage } from '../../../utils/quizReturnMessages';
import { insuranceCompanies } from '../../../config/insuranceCompanies';
import {
  relationshipOptions,
  marriageRelationshipOptions,
  howFoundOptions,
  absenceReasonOptions,
} from '../../../config/quizQuestions';
import { countryPickerData } from '../../../config/countryData';
import {
  CareRecipientData,
  LegalRepresentativeData,
  PreventedCaregiverData,
  SubstituteCaregiverData,
  CommercialProviderData,
  PrivateIndividualData,
  CountryData,
} from '../../../types';
import styles from './EditFieldModal.module.scss';

// Icons
import CloseIcon from '../../../assets/icons/close.svg';
import CalendarIcon from '../../../assets/icons/calendar.svg';
import CheckIcon from '../../../assets/icons/checkPurple.svg';
import ArrowDownIcon from '../../../assets/icons/arrowDown.svg';
import TimeIcon from '../../../assets/icons/time.svg';
import AmountIcon from '../../../assets/icons/amount.svg';
import FlexiLogo from '../../../assets/icons/flexiiLogo.svg';

// Flag icons from country-flag-icons
import DE from 'country-flag-icons/react/3x2/DE';
import AT from 'country-flag-icons/react/3x2/AT';
import CH from 'country-flag-icons/react/3x2/CH';
import NL from 'country-flag-icons/react/3x2/NL';
import BE from 'country-flag-icons/react/3x2/BE';
import FR from 'country-flag-icons/react/3x2/FR';
import IT from 'country-flag-icons/react/3x2/IT';
import PL from 'country-flag-icons/react/3x2/PL';
import ES from 'country-flag-icons/react/3x2/ES';
import GB from 'country-flag-icons/react/3x2/GB';

// Warning messages (must match useQuizAnswer.ts so editing behaves like answering)
const WARNING_MESSAGES = {
  AGE_UNDER_25:
    'Bitte beachten Sie : Bei Antragstellern mit Pflegegrad 4 oder 5 bis einschliesslich 25 Jahre kann die Berechnung Fehler aufweisen.',
  INVALID_CARE_LEVEL:
    'Ersatz von Kosten der Verhinderungspflege ist derzeit nicht möglich, mindestens Pflegegrad 2 ist erforderlich.',
};

// FAQ link shown alongside the invalid-care-level flow-stop message (matches useQuizAnswer.ts)
const INVALID_CARE_LEVEL_LINK = 'https://flexxi.care/de/vhp#faq';

// Private-individual consent question keys (mirrors ConsentCheckboxes.CONSENT_KEYS).
// Cleared when a conditional above them changes during the FILL flow, so the user
// must re-confirm consent before the (now re-opened) form can be submitted —
// matching legacy setEmptyPrivate. NOT cleared while editing from the summary:
// there the consents render as non-interactive checked rows (the interactive
// ConsentCheckboxes UI is hidden), so clearing them would block submit with no way
// to re-check.
const PRIVATE_CONSENT_KEYS = [
  'dataProcessingConsent',
  'termsAgreement',
  'flexxiConsent',
  'paymentConfirmation',
];

// Mapping of question keys to their option lists
const QUESTION_OPTIONS_MAP: Record<string, string[]> = {
  insurance: insuranceCompanies,
  [QUESTION_KEYS.RELATIONSHIP_TYPE]: relationshipOptions,
  [QUESTION_KEYS.MARRIAGE_RELATIONSHIP]: marriageRelationshipOptions,
  [QUESTION_KEYS.HOW_FOUND]: howFoundOptions,
  [QUESTION_KEYS.ABSENCE_REASON]: absenceReasonOptions,
};

// Map country codes to flag components
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const flagComponents: Record<string, any> = {
  DE,
  AT,
  CH,
  NL,
  BE,
  FR,
  IT,
  PL,
  ES,
  GB,
};

// Helper component to render flag
const FlagIcon: React.FC<{ countryCode: string; className?: string }> = ({
  countryCode,
  className,
}) => {
  const FlagComponent = flagComponents[countryCode];
  if (!FlagComponent) return null;
  return <FlagComponent className={className} />;
};

// Register German locale for DatePicker
registerLocale('de', de);

interface EditFieldModalProps {
  fieldKey: string;
  fieldLabel: string;
  currentValue: string;
  section: SectionKey;
  fieldType:
    | 'text'
    | 'number'
    | 'date'
    | 'yesno'
    | 'option'
    | 'rate'
    | 'phone'
    | 'group'
    | 'carePeriod';
  onSave?: () => void;
}

const EditFieldModal: React.FC<EditFieldModalProps> = ({
  fieldKey,
  fieldLabel,
  currentValue,
  section,
  fieldType,
  onSave,
}) => {
  const dispatch = useAppDispatch();
  const quiz = useAppSelector((state) => state.quiz);
  const formData = useAppSelector((state) => state.formData);
  // Don't show "-" (skipped) in input - show empty instead
  const [value, setValue] = useState(currentValue === '-' ? '' : currentValue);
  const [showOptions, setShowOptions] = useState(false);
  const [showCountryDropdown, setShowCountryDropdown] = useState(false);
  const [selectedCountry, setSelectedCountry] = useState<CountryData>(countryPickerData[0]);
  const [phoneNumber, setPhoneNumber] = useState('');

  // Group type state (Date/Dauer/Betrag)
  const [groupDate, setGroupDate] = useState('');
  const [groupDuration, setGroupDuration] = useState('');
  const [groupAmount, setGroupAmount] = useState('');
  const [showGroupCalendar, setShowGroupCalendar] = useState(false);

  // Refs for option list scrolling
  const optionsListRef = useRef<HTMLDivElement>(null);
  const selectedOptionRef = useRef<HTMLDivElement>(null);
  const countryDropdownRef = useRef<HTMLDivElement>(null);
  const groupCalendarRef = useRef<HTMLDivElement>(null);

  // Parse group value (Date | Duration | Amount format)
  useEffect(() => {
    if (fieldType === 'group' && currentValue && currentValue !== '-') {
      const parts = currentValue.split(' | ');
      if (parts.length >= 3) {
        setGroupDate(parts[0] || '');
        // Remove "Std." suffix and "€" prefix
        setGroupDuration(parts[1]?.replace(/\s*Std\.?\s*/gi, '') || '');
        setGroupAmount(parts[2]?.replace(/[€\s]/g, '') || '');
      }
    }
  }, [fieldType, currentValue]);

  // Parse phone number if it has country code
  useEffect(() => {
    if (fieldType === 'phone' && currentValue && currentValue !== '-') {
      // Remove non-digit characters except +
      const digitsOnly = currentValue.replace(/[^\d]/g, '');

      // Try to find matching country code
      const sortedCountries = [...countryPickerData].sort(
        (a, b) => b.callingCode.length - a.callingCode.length
      );

      let phoneWithoutCode = digitsOnly;
      let foundCountry: CountryData | null = null;

      for (const country of sortedCountries) {
        if (digitsOnly.startsWith(country.callingCode)) {
          foundCountry = country;
          phoneWithoutCode = digitsOnly.slice(country.callingCode.length);
          break;
        }
      }

      if (foundCountry) {
        setSelectedCountry(foundCountry);
        setPhoneNumber(phoneWithoutCode);
      } else {
        setPhoneNumber(digitsOnly);
      }
    }
  }, [fieldType, currentValue]);

  useEffect(() => {
    // Don't show "-" (skipped) in input - show empty instead
    setValue(currentValue === '-' ? '' : currentValue);
  }, [currentValue]);

  // Close country dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        countryDropdownRef.current &&
        !countryDropdownRef.current.contains(event.target as Node)
      ) {
        setShowCountryDropdown(false);
      }
      if (groupCalendarRef.current && !groupCalendarRef.current.contains(event.target as Node)) {
        setShowGroupCalendar(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Scroll to selected option when dropdown opens
  useEffect(() => {
    if (showOptions && selectedOptionRef.current && optionsListRef.current) {
      selectedOptionRef.current.scrollIntoView({
        block: 'center',
        behavior: 'auto',
      });
    }
  }, [showOptions]);

  const handleClose = useCallback(() => {
    dispatch(closeModal());
  }, [dispatch]);

  const convertToDate = (dateString: string): Date | null => {
    if (!dateString || dateString.length !== 10) return null;
    const [day, month, year] = dateString.split('.');
    return new Date(Number(year), Number(month) - 1, Number(day));
  };

  // Calendar bounds when editing the absence-period dates. Mirrors the answering
  // flow (QuizInputFooter.getDateBounds) for the July-2025-reform split, plus the
  // cross-date constraint that both dates already exist when editing: the start
  // date can't be after the end date, and the end date can't be before the start
  // date (matches legacy AuthModalComponent).
  const getDateBounds = (): { minDate?: Date; maxDate?: Date } => {
    const today = new Date();
    const JUNE_30_2025 = new Date(2025, 5, 30);
    const JULY_1_2025 = new Date(2025, 6, 1);
    const startDate = convertToDate(formData.preventedCaregiver.absenceStartDate) || undefined;
    const endDate = convertToDate(formData.preventedCaregiver.absenceEndDate) || undefined;

    if (fieldKey === QUESTION_KEYS.ABSENCE_START_DATE) {
      const minDate = quiz.startFromJuly === true ? JULY_1_2025 : undefined;
      // Cap at the flow boundary, and never later than the chosen end date.
      const flowMax = quiz.startFromJuly === false ? JUNE_30_2025 : today;
      const maxDate = endDate && endDate < flowMax ? endDate : flowMax;
      return { minDate, maxDate };
    }

    if (fieldKey === QUESTION_KEYS.ABSENCE_END_DATE) {
      // End date can't be earlier than the start date (fall back to the July
      // lower bound if no start date is set).
      const minDate = startDate || (quiz.startFromJuly === true ? JULY_1_2025 : undefined);
      return { minDate, maxDate: today };
    }

    return { maxDate: today };
  };

  const handleCalendarChange = (date: Date) => {
    const formatted = `${String(date.getDate()).padStart(2, '0')}.${String(date.getMonth() + 1).padStart(2, '0')}.${date.getFullYear()}`;
    setValue(formatted);
  };

  // Helper to reset questions in a specific section
  const resetSectionQuestions = (targetSection: SectionKey, questionKeys: string[]) => {
    questionKeys.forEach((depKey) => {
      dispatch(setQuestionAnswer({ section: targetSection, questionKey: depKey, answer: '' }));
      dispatch(setReturnQuestion({ section: targetSection, questionKey: depKey, message: '' }));

      // Update form data based on target section
      switch (targetSection) {
        case SECTION_KEYS.CARE_RECIPIENT:
          dispatch(setCareRecipientField({ field: depKey as keyof CareRecipientData, value: '' }));
          break;
        case SECTION_KEYS.LEGAL_REPRESENTATIVE:
          dispatch(
            setLegalRepresentativeField({
              field: depKey as keyof LegalRepresentativeData,
              value: '',
            })
          );
          break;
        case SECTION_KEYS.PREVENTED_CAREGIVER:
          dispatch(
            setPreventedCaregiverField({ field: depKey as keyof PreventedCaregiverData, value: '' })
          );
          break;
        case SECTION_KEYS.SUBSTITUTE_CAREGIVER:
          dispatch(
            setSubstituteCaregiverField({
              field: depKey as keyof SubstituteCaregiverData,
              value: '',
            })
          );
          break;
        case SECTION_KEYS.COMMERCIAL_PROVIDER:
          dispatch(
            setCommercialProviderField({
              field: depKey as keyof Omit<CommercialProviderData, 'activeDates'>,
              value: '',
            })
          );
          break;
        case SECTION_KEYS.PRIVATE_INDIVIDUAL:
          dispatch(
            setPrivateIndividualField({
              field: depKey as keyof Omit<
                PrivateIndividualData,
                'individualDates' | 'receiptUpload'
              >,
              value: '',
            })
          );
          break;
      }
    });
  };

  // Reset both downstream "care details" sections (commercial provider AND
  // private individual). Used whenever an answer upstream of them changes the
  // path or the absence period, so stale data from the other branch can't leak
  // into the submission (matches legacy AuthModalComponent / RepModalComponent).
  //
  // A full reset is required, not a per-key blank: each section's quiz questions
  // contain dynamically inserted per-day "availableDates"/"durationOfAbsence"
  // pairs (one per day of the old absence interval). Blanking by questionKey
  // only touches the first matching pair, leaving the extra inserted pairs with
  // stale dates/values and a broken order. So we restore the pristine question
  // template (single pair) AND empty the activeDates/individualDates arrays —
  // mirroring legacy setCommerciallyActive/setPrivateIndividual([...template])
  // + setEmptyActive/setEmptyPrivate.
  const resetCommercialAndPrivateSections = () => {
    dispatch(resetCommercialProvider());
    dispatch(resetSection(SECTION_KEYS.COMMERCIAL_PROVIDER));

    dispatch(resetPrivateIndividual());
    dispatch(resetSection(SECTION_KEYS.PRIVATE_INDIVIDUAL));
  };

  // Reset a list of Private-Individual questions (those downstream of an edited
  // Ja/Nein conditional) in BOTH quiz and form-data state. Mirrors legacy
  // setEmptyPrivate, which wipes the whole tail below a changed conditional so
  // stale data from the previous path can't survive into the submission.
  // receiptUpload is an array field, so it is reset to [] rather than '' (the
  // generic setPrivateIndividualField only handles string fields).
  const resetPrivateIndividualKeys = (keys: string[]) => {
    keys.forEach((key) => {
      dispatch(
        setQuestionAnswer({
          section: SECTION_KEYS.PRIVATE_INDIVIDUAL,
          questionKey: key,
          answer: '',
        })
      );
      dispatch(
        setReturnQuestion({
          section: SECTION_KEYS.PRIVATE_INDIVIDUAL,
          questionKey: key,
          message: '',
        })
      );
    });

    const formReset: Partial<PrivateIndividualData> = {};
    keys.forEach((key) => {
      if (key === QUESTION_KEYS.RECEIPT_UPLOAD) {
        formReset.receiptUpload = [];
      } else {
        (formReset as Record<string, string>)[key] = '';
      }
    });
    dispatch(setPrivateIndividual(formReset));
  };

  // Reset dependent questions when a parent answer changes
  const resetDependentQuestions = (parentKey: string, newAnswer: string) => {
    // Get the current section's questions to find indices
    const sectionData = quiz[section as keyof typeof quiz];
    const questions =
      sectionData && typeof sectionData === 'object' && 'questions' in sectionData
        ? (sectionData as { questions: { questionKey: string }[] }).questions
        : [];
    const parentIndex = questions.findIndex((q) => q.questionKey === parentKey);

    // ============================================================
    // SCENARIO 0: Care Recipient - levelOfCare (Pflegegrad)
    // When changed to "Nein" or "1": reimbursement is not possible, so the flow
    // stops here. Hide/clear the dependent care questions (certificate,
    // legalRepresentation) and the entire legal-representative section so a
    // previously-filled branch can't survive, then show the flow-stop message
    // + FAQ link (matches legacy CareModalComponent + findFirstUnansweredIndex).
    // When changed back to a valid level (2-5): clear the flow-stop message so
    // the flow can resume.
    // ============================================================
    if (parentKey === QUESTION_KEYS.LEVEL_OF_CARE) {
      const isInvalidLevel = newAnswer === CARE_LEVELS.NONE || newAnswer === CARE_LEVELS.LEVEL_1;

      if (isInvalidLevel) {
        // Clear the dependent care-recipient questions (kept empty, not "-", to
        // match the inline skip handling in useQuizAnswer.ts).
        resetSectionQuestions(SECTION_KEYS.CARE_RECIPIENT, [
          QUESTION_KEYS.CERTIFICATE,
          QUESTION_KEYS.LEGAL_REPRESENTATION,
        ]);
        // Reset the legal-representative section entirely.
        const legalRepKeys = [
          'lastName',
          'name',
          'address',
          'houseNumber',
          'zipCode',
          'city',
          'phoneNumber',
          'document',
        ];
        resetSectionQuestions(SECTION_KEYS.LEGAL_REPRESENTATIVE, legalRepKeys);
        dispatch(setCurrentIndex({ section: SECTION_KEYS.LEGAL_REPRESENTATIVE, index: 0 }));
        // Keep the care-recipient cursor on the levelOfCare question (the stop point).
        if (parentIndex >= 0) {
          dispatch(setCurrentIndex({ section: section as SectionKey, index: parentIndex }));
        }
        // Show the flow-stop message + link (returnLink is set so QuizSection
        // defers rendering to the ChallengeStepsContent flow-stop banner).
        dispatch(
          setReturnQuestion({
            section: section as SectionKey,
            questionKey: parentKey,
            message: WARNING_MESSAGES.INVALID_CARE_LEVEL,
            link: INVALID_CARE_LEVEL_LINK,
          })
        );
      } else {
        // Valid care level: clear any prior flow-stop message/link.
        dispatch(
          setReturnQuestion({
            section: section as SectionKey,
            questionKey: parentKey,
            message: '',
            link: '',
          })
        );
      }
      return;
    }

    // ============================================================
    // SCENARIO 1: Care Recipient - legalRepresentation (isBetreuung)
    // When changed from "Nein" to "Ja": reset legalRepresentative AND preventedCaregiver sections
    // When changed from "Ja" to "Nein": reset only legalRepresentative section
    // ============================================================
    if (parentKey === QUESTION_KEYS.LEGAL_REPRESENTATION) {
      // Always reset legalRepresentative section
      const legalRepKeys = [
        'lastName',
        'name',
        'address',
        'houseNumber',
        'zipCode',
        'city',
        'phoneNumber',
        'document',
      ];
      resetSectionQuestions(SECTION_KEYS.LEGAL_REPRESENTATIVE, legalRepKeys);
      dispatch(setCurrentIndex({ section: SECTION_KEYS.LEGAL_REPRESENTATIVE, index: 0 }));

      // If changing to "Ja" (adding legal representative), also reset preventedCaregiver section
      // because user will fill legal representative first, then continue to preventedCaregiver
      if (newAnswer === ANSWERS.YES) {
        const preventedKeys = [
          'lastName',
          'name',
          'birthday',
          'address',
          'houseNumber',
          'zipCode',
          'city',
          'phoneNumber',
          QUESTION_KEYS.IS_PROFESSIONAL_CARER,
          QUESTION_KEYS.CARE_PERIOD,
          QUESTION_KEYS.ABSENCE_START_DATE,
          QUESTION_KEYS.ABSENCE_END_DATE,
          QUESTION_KEYS.ABSENCE_REASON,
        ];
        resetSectionQuestions(SECTION_KEYS.PREVENTED_CAREGIVER, preventedKeys);
        dispatch(setCurrentIndex({ section: SECTION_KEYS.PREVENTED_CAREGIVER, index: 0 }));
      }
      return;
    }

    // ============================================================
    // SCENARIO 2: Prevented Caregiver - isProfessionalCarer
    // Reset carePeriod and absenceStartDate
    // ============================================================
    if (parentKey === QUESTION_KEYS.IS_PROFESSIONAL_CARER) {
      resetSectionQuestions(SECTION_KEYS.PREVENTED_CAREGIVER, [
        QUESTION_KEYS.CARE_PERIOD,
        QUESTION_KEYS.ABSENCE_START_DATE,
        QUESTION_KEYS.ABSENCE_END_DATE,
        QUESTION_KEYS.ABSENCE_REASON,
      ]);
      if (parentIndex >= 0) {
        dispatch(setCurrentIndex({ section: section as SectionKey, index: parentIndex }));
      }
      // The "Ja" flow-stop message is rendered once by the ChallengeStepsContent
      // banner (driven by formData), exactly like the inline-answer path. Clear
      // any inline returnQuestion here so the message isn't shown twice.
      dispatch(
        setReturnQuestion({ section: section as SectionKey, questionKey: parentKey, message: '' })
      );
      return;
    }

    // ============================================================
    // SCENARIO 3: Prevented Caregiver - carePeriod
    // Reset absenceStartDate and subsequent
    // ============================================================
    if (parentKey === QUESTION_KEYS.CARE_PERIOD) {
      resetSectionQuestions(SECTION_KEYS.PREVENTED_CAREGIVER, [
        QUESTION_KEYS.ABSENCE_START_DATE,
        QUESTION_KEYS.ABSENCE_END_DATE,
        QUESTION_KEYS.ABSENCE_REASON,
      ]);
      if (parentIndex >= 0) {
        dispatch(setCurrentIndex({ section: section as SectionKey, index: parentIndex }));
      }
      // The "Unter 6 Monate" flow-stop message is rendered once by the
      // ChallengeStepsContent banner (driven by formData), exactly like the
      // inline-answer path. Clear any inline returnQuestion here so the message
      // isn't shown twice (double message).
      dispatch(
        setReturnQuestion({ section: section as SectionKey, questionKey: parentKey, message: '' })
      );
      return;
    }

    // ============================================================
    // SCENARIO 4: Prevented Caregiver - absenceStartDate or absenceEndDate
    // Reset BOTH privateIndividual AND commercialProvider sections entirely
    // ============================================================
    if (
      parentKey === QUESTION_KEYS.ABSENCE_START_DATE ||
      parentKey === QUESTION_KEYS.ABSENCE_END_DATE
    ) {
      // Changing the absence period invalidates all care-session details and the
      // per-day questions generated from the old interval, so reset both
      // downstream sections to their pristine templates.
      resetCommercialAndPrivateSections();

      // Recompute the available-days count from the NEW interval so the correct
      // number of per-day date questions is generated as the user re-fills
      // (matches the inline-answer path in useQuizAnswer). parentKey holds the
      // edited date; read the other end from the still-current form data.
      const newStart =
        parentKey === QUESTION_KEYS.ABSENCE_START_DATE
          ? newAnswer
          : formData.preventedCaregiver.absenceStartDate;
      const newEnd =
        parentKey === QUESTION_KEYS.ABSENCE_END_DATE
          ? newAnswer
          : formData.preventedCaregiver.absenceEndDate;
      const start = convertToDate(newStart);
      const end = convertToDate(newEnd);
      let days = 0;
      if (start && end) {
        days = Math.ceil((end.getTime() - start.getTime() + 1) / (1000 * 3600 * 24));
      }
      dispatch(setAvailableDays(days > 0 ? String(days) : ''));
      return;
    }

    // ============================================================
    // SCENARIO 5: Substitute Caregiver - isPaidByOthers
    // This answer chooses the whole downstream flow: "Ja" → commercial provider,
    // "Nein" → private individual. Changing it must reset the substitute tail
    // AND both care-detail sections, so the previous branch's data can't survive
    // (matches legacy RepModalComponent which resets private + commercial here).
    // ============================================================
    if (parentKey === QUESTION_KEYS.IS_PAID_BY_OTHERS) {
      resetSectionQuestions(SECTION_KEYS.SUBSTITUTE_CAREGIVER, [
        QUESTION_KEYS.IS_COMMERCIALLY_ACTIVE,
        QUESTION_KEYS.RELATIONSHIP_TYPE,
        QUESTION_KEYS.MARRIAGE_RELATIONSHIP,
        QUESTION_KEYS.SAME_HOUSEHOLD,
        QUESTION_KEYS.HOW_FOUND,
      ]);
      resetCommercialAndPrivateSections();
      if (parentIndex >= 0) {
        dispatch(setCurrentIndex({ section: section as SectionKey, index: parentIndex }));
      }
      return;
    }

    // ============================================================
    // SCENARIO 6: Substitute Caregiver - isCommerciallyActive
    // Reset relationship questions
    // ============================================================
    if (parentKey === QUESTION_KEYS.IS_COMMERCIALLY_ACTIVE) {
      resetSectionQuestions(SECTION_KEYS.SUBSTITUTE_CAREGIVER, [
        QUESTION_KEYS.RELATIONSHIP_TYPE,
        QUESTION_KEYS.MARRIAGE_RELATIONSHIP,
        QUESTION_KEYS.SAME_HOUSEHOLD,
        QUESTION_KEYS.HOW_FOUND,
      ]);
      if (parentIndex >= 0) {
        dispatch(setCurrentIndex({ section: section as SectionKey, index: parentIndex }));
      }
      return;
    }

    // ============================================================
    // SCENARIO 7: Private Individual - hasTravelCosts
    // Changing the travel-cost Ja/Nein invalidates the rest of the private-
    // individual flow. During the FILL flow we reset the whole tail below it
    // (travel block + the earnings-loss question & amount + bank details +
    // consents) and restart here, matching legacy IndividualModalComponent, which
    // rebuilds the question list from hasTravelCosts and clears the tail via
    // setEmptyPrivate. When editing from the SUMMARY we only reset the immediate
    // travel block: the form there is already complete, the submit button is
    // gated solely by areAllConsentsChecked, and the editing-mode consents are
    // not re-checkable (ConsentCheckboxes is hidden) — so wiping the tail/consents
    // would either allow an incomplete submit or dead-end the user.
    // ============================================================
    if (parentKey === QUESTION_KEYS.HAS_TRAVEL_COSTS) {
      const travelBlockKeys = [
        QUESTION_KEYS.DISTANCE_KM,
        QUESTION_KEYS.ORIGIN_LOCATION,
        QUESTION_KEYS.DESTINATION_LOCATION,
        QUESTION_KEYS.TRAVEL_COST,
        QUESTION_KEYS.RECEIPT_UPLOAD,
      ];
      resetPrivateIndividualKeys(
        quiz.isEditing
          ? travelBlockKeys
          : [
              ...travelBlockKeys,
              QUESTION_KEYS.HAS_EARNINGS_LOSS,
              QUESTION_KEYS.EARNINGS_LOSS_AMOUNT,
              QUESTION_KEYS.ACCOUNT_HOLDER_NAME,
              QUESTION_KEYS.IBAN,
              QUESTION_KEYS.BANK,
              ...PRIVATE_CONSENT_KEYS,
            ]
      );
      if (parentIndex >= 0) {
        dispatch(setCurrentIndex({ section: section as SectionKey, index: parentIndex }));
      }
      return;
    }

    // ============================================================
    // SCENARIO 8: Private Individual - hasEarningsLoss
    // Changing the earnings-loss Ja/Nein invalidates everything below it. During
    // the FILL flow we reset the amount + bank details + consents and restart here
    // (matches legacy setEmptyPrivate for the Verdienstausfall branch). When
    // editing from the SUMMARY we only reset the amount, for the same reasons as
    // SCENARIO 7.
    // ============================================================
    if (parentKey === QUESTION_KEYS.HAS_EARNINGS_LOSS) {
      resetPrivateIndividualKeys(
        quiz.isEditing
          ? [QUESTION_KEYS.EARNINGS_LOSS_AMOUNT]
          : [
              QUESTION_KEYS.EARNINGS_LOSS_AMOUNT,
              QUESTION_KEYS.ACCOUNT_HOLDER_NAME,
              QUESTION_KEYS.IBAN,
              QUESTION_KEYS.BANK,
              ...PRIVATE_CONSENT_KEYS,
            ]
      );
      if (parentIndex >= 0) {
        dispatch(setCurrentIndex({ section: section as SectionKey, index: parentIndex }));
      }
      return;
    }
  };

  const handleSave = () => {
    // Handle group type - save as object
    if (fieldType === 'group') {
      const groupAnswer = {
        date: groupDate || '-',
        duration: groupDuration ? `${groupDuration} Std.` : '0 Std.',
        amount: groupAmount ? `€ ${groupAmount}` : '€ 0',
      };

      // Update quiz state with object answer
      dispatch(setQuestionAnswer({ section, questionKey: fieldKey, answer: groupAnswer }));

      // Update form data with formatted string
      const formattedValue = `${groupAnswer.date} | ${groupAnswer.duration} | ${groupAnswer.amount}`;
      switch (section) {
        case SECTION_KEYS.COMMERCIAL_PROVIDER:
          dispatch(
            setCommercialProviderField({
              field: fieldKey as keyof Omit<CommercialProviderData, 'activeDates'>,
              value: formattedValue,
            })
          );
          break;
        case SECTION_KEYS.PRIVATE_INDIVIDUAL:
          dispatch(
            setPrivateIndividualField({
              field: fieldKey as keyof Omit<
                PrivateIndividualData,
                'individualDates' | 'receiptUpload'
              >,
              value: formattedValue,
            })
          );
          break;
      }

      onSave?.();
      handleClose();
      return;
    }

    // Determine the final value - for phone, combine country code with number
    const finalValue =
      fieldType === 'phone' && phoneNumber
        ? `+${selectedCountry.callingCode}${phoneNumber}`
        : value;

    // CRITICAL: Reset dependent questions if the answer changed
    // This ensures dependent questions are asked again when parent answer changes
    if (finalValue !== currentValue) {
      resetDependentQuestions(fieldKey, finalValue);
    }

    // Update quiz state
    dispatch(setQuestionAnswer({ section, questionKey: fieldKey, answer: finalValue }));

    // Check for age-based warning message when birthday is edited
    // This ensures the warning is shown/hidden when editing birthday to age < 25 or >= 25
    if (section === SECTION_KEYS.CARE_RECIPIENT && fieldKey === QUESTION_KEYS.BIRTHDAY) {
      const age = calculateAge(finalValue);
      if (age < 25) {
        dispatch(
          setReturnQuestion({
            section,
            questionKey: fieldKey,
            message: WARNING_MESSAGES.AGE_UNDER_25,
          })
        );
      } else {
        // Clear any existing warning if age is >= 25
        dispatch(
          setReturnQuestion({
            section,
            questionKey: fieldKey,
            message: '',
          })
        );
      }
    }

    // Update dynamic info/warning messages so they appear or clear when the
    // answer is edited — matching the inline flow. Always dispatch: an empty
    // result clears a stale message. For preventedCaregiver only absenceReason
    // is included; isProfessionalCarer / carePeriod flow-stops are rendered by
    // the ChallengeStepsContent banner (not an inline returnQuestion), so they
    // are intentionally excluded here.
    const hasDynamicMessage =
      section === SECTION_KEYS.SUBSTITUTE_CAREGIVER ||
      section === SECTION_KEYS.PRIVATE_INDIVIDUAL ||
      (section === SECTION_KEYS.PREVENTED_CAREGIVER && fieldKey === QUESTION_KEYS.ABSENCE_REASON);
    if (hasDynamicMessage) {
      dispatch(
        setReturnQuestion({
          section,
          questionKey: fieldKey,
          message: getReturnMessage(section, fieldKey, finalValue),
        })
      );
    }

    // Update form data based on section
    switch (section) {
      case SECTION_KEYS.CARE_RECIPIENT:
        dispatch(
          setCareRecipientField({ field: fieldKey as keyof CareRecipientData, value: finalValue })
        );
        break;
      case SECTION_KEYS.LEGAL_REPRESENTATIVE:
        dispatch(
          setLegalRepresentativeField({
            field: fieldKey as keyof LegalRepresentativeData,
            value: finalValue,
          })
        );
        break;
      case SECTION_KEYS.PREVENTED_CAREGIVER:
        dispatch(
          setPreventedCaregiverField({
            field: fieldKey as keyof PreventedCaregiverData,
            value: finalValue,
          })
        );
        break;
      case SECTION_KEYS.SUBSTITUTE_CAREGIVER:
        dispatch(
          setSubstituteCaregiverField({
            field: fieldKey as keyof SubstituteCaregiverData,
            value: finalValue,
          })
        );
        break;
      case SECTION_KEYS.COMMERCIAL_PROVIDER:
        dispatch(
          setCommercialProviderField({
            field: fieldKey as keyof Omit<CommercialProviderData, 'activeDates'>,
            value: finalValue,
          })
        );
        break;
      case SECTION_KEYS.PRIVATE_INDIVIDUAL:
        dispatch(
          setPrivateIndividualField({
            field: fieldKey as keyof Omit<
              PrivateIndividualData,
              'individualDates' | 'receiptUpload'
            >,
            value: finalValue,
          })
        );
        break;
    }

    onSave?.();
    handleClose();
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSave();
    } else if (e.key === 'Escape') {
      handleClose();
    }
  };

  const renderInput = () => {
    switch (fieldType) {
      case 'text':
        return (
          <input
            type="text"
            value={value}
            onChange={(e) => setValue(e.target.value)}
            onKeyDown={handleKeyDown}
            className={styles.textInput}
            autoFocus
          />
        );

      case 'phone':
        return (
          <div className={styles.phoneWrapper} ref={countryDropdownRef}>
            <div className={styles.countrySelector}>
              <button
                type="button"
                className={styles.countrySelectorBtn}
                onClick={() => setShowCountryDropdown(!showCountryDropdown)}
              >
                <FlagIcon countryCode={selectedCountry.key} className={styles.flagIcon} />
                <span>+{selectedCountry.callingCode}</span>
                <img src={ArrowDownIcon} alt="" className={styles.arrowDown} />
              </button>
              {showCountryDropdown && (
                <div className={styles.countryDropdown}>
                  {countryPickerData.map((country) => (
                    <button
                      key={country.key}
                      type="button"
                      className={cx(styles.countryOption, {
                        [styles.selected]: country.key === selectedCountry.key,
                      })}
                      onClick={() => {
                        setSelectedCountry(country);
                        setShowCountryDropdown(false);
                      }}
                    >
                      <FlagIcon countryCode={country.key} className={styles.flagIcon} />
                      <span className={styles.countryName}>{country.nameDE}</span>
                      <span className={styles.callingCode}>+{country.callingCode}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
            <input
              type="tel"
              value={phoneNumber}
              onChange={(e) => {
                const numericValue = e.target.value.replace(/\D/g, '');
                if (numericValue.length <= selectedCountry.maxLength) {
                  setPhoneNumber(numericValue);
                }
              }}
              onKeyDown={handleKeyDown}
              className={styles.phoneInput}
              placeholder="Telefonnummer"
              autoFocus
            />
          </div>
        );

      case 'number':
        return (
          <input
            type="text"
            inputMode="numeric"
            value={value}
            onChange={(e) => {
              if (!/^\d*$/.test(e.target.value)) return;
              // Free-length numeric fields (km, travel cost, earnings loss) have no max.
              const isFreeLength = FREE_LENGTH_NUMBER_KEYS.includes(fieldKey);
              if (!isFreeLength && e.target.value.length > VALIDATION.ZIP_CODE_LENGTH) return;
              setValue(e.target.value);
            }}
            onKeyDown={handleKeyDown}
            className={styles.textInput}
            autoFocus
          />
        );

      case 'date': {
        const dateBounds = getDateBounds();
        // Open the calendar within the allowed range so the user lands on a
        // selectable month rather than one where every day is disabled.
        const clampOpenDate = (d: Date): Date => {
          if (dateBounds.maxDate && d > dateBounds.maxDate) return dateBounds.maxDate;
          if (dateBounds.minDate && d < dateBounds.minDate) return dateBounds.minDate;
          return d;
        };
        const openDate =
          convertToDate(value) ||
          clampOpenDate(new Date(1965, new Date().getMonth(), new Date().getDate()));
        return (
          <div className={styles.datePickerWrapper}>
            <DatePicker
              selected={convertToDate(value)}
              onChange={(date: Date | null) => date && handleCalendarChange(date)}
              dateFormat="dd.MM.yyyy"
              locale="de"
              minDate={dateBounds.minDate}
              maxDate={dateBounds.maxDate}
              showYearDropdown
              showMonthDropdown
              dropdownMode="select"
              yearDropdownItemNumber={100}
              scrollableYearDropdown
              placeholderText="TT.MM.JJJJ"
              className={styles.dateInput}
              calendarClassName={styles.datepickerCalendar}
              onKeyDown={(e) => e.preventDefault()}
              openToDate={openDate}
              showIcon
              icon={<img src={CalendarIcon} alt="Calendar" className={styles.calendarIcon} />}
              toggleCalendarOnIconClick
              onChangeRaw={(e) => e?.preventDefault()}
            />
          </div>
        );
      }

      case 'yesno':
        return (
          <div className={styles.yesNoGroup}>
            <button
              type="button"
              className={cx(styles.yesNoButton, { [styles.selected]: value === 'Ja' })}
              onClick={() => setValue('Ja')}
            >
              Ja
            </button>
            <button
              type="button"
              className={cx(styles.yesNoButton, { [styles.selected]: value === 'Nein' })}
              onClick={() => setValue('Nein')}
            >
              Nein
            </button>
          </div>
        );

      case 'carePeriod':
        return (
          <div className={styles.yesNoGroup}>
            <button
              type="button"
              className={cx(styles.yesNoButton, {
                [styles.selected]: value === CARE_PERIODS.UNDER_6_MONTHS,
              })}
              onClick={() => setValue(CARE_PERIODS.UNDER_6_MONTHS)}
            >
              {CARE_PERIODS.UNDER_6_MONTHS}
            </button>
            <button
              type="button"
              className={cx(styles.yesNoButton, {
                [styles.selected]: value === CARE_PERIODS.OVER_6_MONTHS,
              })}
              onClick={() => setValue(CARE_PERIODS.OVER_6_MONTHS)}
            >
              {CARE_PERIODS.OVER_6_MONTHS}
            </button>
          </div>
        );

      case 'rate':
        const rateOptions = ['Nein', '1', '2', '3', '4', '5'];
        return (
          <div className={styles.rateGroup}>
            {rateOptions.map((option) => (
              <button
                key={option}
                type="button"
                className={cx(styles.rateButton, { [styles.selected]: value === option })}
                onClick={() => setValue(option)}
              >
                {option}
              </button>
            ))}
          </div>
        );

      case 'option':
        // Get the correct options list based on field key
        const editOptionsList = QUESTION_OPTIONS_MAP[fieldKey] || [];
        return (
          <div className={styles.optionWrapper}>
            <div
              className={cx(styles.select, { [styles.hasValue]: value })}
              onClick={() => setShowOptions(!showOptions)}
            >
              {value || 'Bitte wählen Sie'}
            </div>
            {showOptions && (
              <div className={styles.optionList} ref={optionsListRef}>
                {editOptionsList
                  .sort((a, b) => a.localeCompare(b))
                  .map((item) => (
                    <div
                      key={item}
                      ref={item === value ? selectedOptionRef : null}
                      className={cx(styles.optionItem, { [styles.selectedOption]: item === value })}
                      onClick={() => {
                        setValue(item);
                        setShowOptions(false);
                      }}
                    >
                      <span>{item}</span>
                      {item === value && (
                        <img src={CheckIcon} alt="" className={styles.checkIcon} />
                      )}
                    </div>
                  ))}
              </div>
            )}
          </div>
        );

      case 'group':
        return (
          <div className={styles.groupEditWrapper}>
            <div className={styles.groupField} ref={groupCalendarRef}>
              <input
                type="text"
                placeholder="Datum"
                value={groupDate}
                readOnly
                className={styles.groupInput}
                onClick={() => setShowGroupCalendar(!showGroupCalendar)}
              />
              <img
                src={CalendarIcon}
                alt="Kalender"
                onClick={() => setShowGroupCalendar(!showGroupCalendar)}
                className={styles.groupFieldIcon}
              />
              {showGroupCalendar && (
                <div className={styles.groupCalendarPopup}>
                  <DatePicker
                    selected={
                      groupDate ? new Date(groupDate.split('.').reverse().join('-')) : new Date()
                    }
                    onChange={(date: Date | null) => {
                      if (date) {
                        const day = String(date.getDate()).padStart(2, '0');
                        const month = String(date.getMonth() + 1).padStart(2, '0');
                        const year = date.getFullYear();
                        setGroupDate(`${day}.${month}.${year}`);
                        setShowGroupCalendar(false);
                      }
                    }}
                    inline
                    locale="de"
                    dateFormat="dd.MM.yyyy"
                    calendarClassName="quiz-datepicker"
                  />
                </div>
              )}
            </div>
            <div className={styles.groupField}>
              <input
                type="text"
                placeholder="0"
                value={groupDuration}
                onChange={(e) => {
                  // Allow only numbers with one decimal place
                  const val = e.target.value;
                  if (/^\d*\.?\d{0,1}$/.test(val)) {
                    const numVal = parseFloat(val);
                    if (val === '' || (numVal >= 0 && numVal <= 24)) {
                      setGroupDuration(val);
                    }
                  }
                }}
                className={styles.groupInput}
              />
              <img src={TimeIcon} alt="Zeit" className={styles.groupFieldIcon} />
            </div>
            <div className={styles.groupField}>
              <input
                type="text"
                placeholder="0"
                value={groupAmount}
                onChange={(e) => {
                  // Allow only whole numbers
                  if (/^\d*$/.test(e.target.value)) {
                    setGroupAmount(e.target.value);
                  }
                }}
                className={styles.groupInput}
              />
              <img src={AmountIcon} alt="Betrag" className={styles.groupFieldIcon} />
            </div>
          </div>
        );

      default:
        return (
          <input
            type="text"
            value={value}
            onChange={(e) => setValue(e.target.value)}
            onKeyDown={handleKeyDown}
            className={styles.textInput}
            autoFocus
          />
        );
    }
  };

  return (
    <div className={styles.modalOverlay}>
      <div
        className={styles.modal}
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
      >
        <div className={styles.header}>
          <h3>Antwort bearbeiten</h3>
          <button
            type="button"
            className={styles.closeButton}
            onClick={handleClose}
            aria-label="Schließen"
          >
            <img src={CloseIcon} alt="" />
          </button>
        </div>

        <div className={styles.content}>
          {fieldType === 'yesno' || fieldType === 'carePeriod' ? (
            <div className={styles.chatStyleLabel}>
              <img src={FlexiLogo} alt="Flexxi" className={styles.chatLogo} />
              <p className={styles.chatQuestion}>{fieldLabel}</p>
            </div>
          ) : (
            <label className={styles.fieldLabel}>{fieldLabel}</label>
          )}
          {renderInput()}
        </div>

        <div className={styles.footer}>
          <button type="button" className={styles.cancelButton} onClick={handleClose}>
            Abbrechen
          </button>
          <button type="button" className={styles.saveButton} onClick={handleSave}>
            Speichern
          </button>
        </div>
      </div>
    </div>
  );
};

export default EditFieldModal;
